import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { TransferItem, TransferResponse } from './transfer.config';

@Injectable({
  providedIn: 'root',
})
export class TransferService {
  // Mock data for development
  private readonly mockTransfers: TransferItem[] = [
    {
      id: '1',
      serialNumber: 1,
      sourceWarehouse: 'Default Virtual Warehouse',
      transferQuantity: 25,
      destinationWarehouse: 'Quantity Virtual Warehouse',
      remark: '',
      transferOrderNumber: 'TR-2024-001',
      status: 'wait',
      createdBy: 'System Admin',
      createdAt: '2024-11-28 09:39:05',
    },
    {
      id: '2',
      serialNumber: 2,
      sourceWarehouse: 'Default Virtual Warehouse',
      transferQuantity: 90,
      destinationWarehouse: 'Interline Virtual Warehouse',
      remark: '',
      transferOrderNumber: 'TR-2024-002',
      status: 'wait',
      createdBy: 'Warehouse Manager',
      createdAt: '2024-09-09 04:52:39',
    },
    {
      id: '3',
      serialNumber: 3,
      sourceWarehouse: 'Quantity Virtual Warehouse',
      transferQuantity: 150,
      destinationWarehouse: 'Default Virtual Warehouse',
      remark: 'Bulk transfer for Q4',
      transferOrderNumber: 'TR-2024-003',
      status: 'wait-execute',
      createdBy: 'System Admin',
      createdAt: '2024-11-27 14:20:30',
    },
    {
      id: '4',
      serialNumber: 4,
      sourceWarehouse: 'Interline Virtual Warehouse',
      transferQuantity: 75,
      destinationWarehouse: 'Quantity Virtual Warehouse',
      remark: 'Seasonal adjustment',
      transferOrderNumber: 'TR-2024-004',
      status: 'executed',
      createdBy: 'Warehouse Manager',
      createdAt: '2024-11-26 10:15:45',
    },
    {
      id: '5',
      serialNumber: 5,
      sourceWarehouse: 'Default Virtual Warehouse',
      transferQuantity: 200,
      destinationWarehouse: 'Interline Virtual Warehouse',
      remark: 'Inventory rebalancing',
      transferOrderNumber: 'TR-2024-005',
      status: 'execute-fail',
      createdBy: 'Inventory Controller',
      createdAt: '2024-11-25 16:30:20',
    },
    {
      id: '6',
      serialNumber: 6,
      sourceWarehouse: 'Quantity Virtual Warehouse',
      transferQuantity: 120,
      destinationWarehouse: 'Default Virtual Warehouse',
      remark: 'Failed transfer',
      transferOrderNumber: 'TR-2024-006',
      status: 'fail',
      createdBy: 'System Admin',
      createdAt: '2024-11-24 12:00:00',
    },
    {
      id: '7',
      serialNumber: 7,
      sourceWarehouse: 'Default Virtual Warehouse',
      transferQuantity: 80,
      destinationWarehouse: 'Interline Virtual Warehouse',
      remark: 'Cancelled transfer',
      transferOrderNumber: 'TR-2024-007',
      status: 'cancel',
      createdBy: 'Warehouse Manager',
      createdAt: '2024-11-23 10:30:00',
    },
  ];

  constructor() {}

  getTransfers(
    page: number = 1,
    pageSize: number = 20,
    searchCriteria?: any
  ): Observable<TransferResponse> {
    // Simulate API delay
    return of(this.getFilteredTransfers(searchCriteria)).pipe(delay(500));
  }

  getTransferById(id: string): Observable<TransferItem | null> {
    const transfer = this.mockTransfers.find((t) => t.id === id);
    return of(transfer || null).pipe(delay(300));
  }

  // Execute transfer
  executeTransfer(id: string): Observable<any> {
    const transfer = this.mockTransfers.find((t) => t.id === id);
    if (transfer) {
      if (transfer.status === 'wait') {
        transfer.status = 'wait-execute';
      } else if (transfer.status === 'wait-execute') {
        transfer.status = 'executed';
      } else if (transfer.status === 'execute-fail') {
        transfer.status = 'wait-execute';
      }
    }
    return of({
      success: true,
      message: 'Transfer executed successfully',
    }).pipe(delay(800));
  }

  // Cancel transfer
  cancelTransfer(id: string): Observable<any> {
    const transfer = this.mockTransfers.find((t) => t.id === id);
    if (transfer && transfer.status === 'wait') {
      transfer.status = 'cancel';
    }
    return of({
      success: true,
      message: 'Transfer cancelled successfully',
    }).pipe(delay(600));
  }

  // Invalidate transfer
  invalidateTransfer(id: string): Observable<any> {
    const transfer = this.mockTransfers.find((t) => t.id === id);
    if (
      transfer &&
      ['wait-execute', 'execute-fail'].includes(transfer.status)
    ) {
      transfer.status = 'fail';
    }
    return of({
      success: true,
      message: 'Transfer invalidated successfully',
    }).pipe(delay(600));
  }

  // Reset transfer
  resetTransfer(id: string): Observable<any> {
    const transfer = this.mockTransfers.find((t) => t.id === id);
    if (transfer && ['fail', 'cancel'].includes(transfer.status)) {
      transfer.status = 'wait';
    }
    return of({ success: true, message: 'Transfer reset successfully' }).pipe(
      delay(600)
    );
  }

  // Approve transfer (audit mode)
  approveTransfer(id: string): Observable<any> {
    const transfer = this.mockTransfers.find((t) => t.id === id);
    if (transfer && transfer.status === 'wait') {
      transfer.status = 'wait-execute';
    }
    return of({
      success: true,
      message: 'Transfer approved successfully',
    }).pipe(delay(600));
  }

  // Reject transfer (audit mode)
  rejectTransfer(id: string): Observable<any> {
    const transfer = this.mockTransfers.find((t) => t.id === id);
    if (transfer && transfer.status === 'wait') {
      transfer.status = 'fail';
    }
    return of({
      success: true,
      message: 'Transfer rejected successfully',
    }).pipe(delay(600));
  }

  refreshData(): void {
    // In real implementation, this would trigger a refresh
    console.log('Refreshing virtual transfer data...');
  }

  private getFilteredTransfers(searchCriteria?: any): TransferResponse {
    let filteredData = [...this.mockTransfers];

    if (searchCriteria) {
      // Apply search filters
      if (searchCriteria.transferOrderNumber) {
        filteredData = filteredData.filter((t) =>
          t.transferOrderNumber
            ?.toLowerCase()
            .includes(searchCriteria.transferOrderNumber.toLowerCase())
        );
      }

      if (searchCriteria.sourceWarehouse) {
        filteredData = filteredData.filter(
          (t) => t.sourceWarehouse === searchCriteria.sourceWarehouse
        );
      }

      if (searchCriteria.destinationWarehouse) {
        filteredData = filteredData.filter(
          (t) => t.destinationWarehouse === searchCriteria.destinationWarehouse
        );
      }

      if (searchCriteria.status) {
        filteredData = filteredData.filter(
          (t) => t.status === searchCriteria.status
        );
      }

      if (searchCriteria.beginCreationDate && searchCriteria.endCreationDate) {
        const startDate = new Date(searchCriteria.beginCreationDate);
        const endDate = new Date(searchCriteria.endCreationDate);
        filteredData = filteredData.filter((t) => {
          const createdDate = new Date(t.createdAt);
          return createdDate >= startDate && createdDate <= endDate;
        });
      }
    }

    return {
      data: filteredData,
      totalRecords: filteredData.length,
      loading: false,
    };
  }
}
